/**
 * One mail thread (`/{ws}/mail/t/{threadId}`, logic/mail): its messages oldest first, each message's sanitized HTML
 * (fetched on demand; remote images blocked until "Load images"), the auth/phishing warnings, and what a reply,
 * reply all or forward should start with (MailComposerViewModel takes it). Opening it marks it read on the server.
 * A thread I've read opens from the device (MailThreadCache), bodies included, and then refreshes in place: nothing
 * blanks, a failed refresh changes nothing, and a message that came in the meantime arrives with an accent.
 */
import type { AnswerMailInviteRequest, MailBodyView, MailDraft, MailMessage, MailReplyFrom, MailSendAs, MailThread, PersistentEvent } from "@bundle/contracts";
import { FORWARD_QUOTE_MAX, REPLY_QUOTE_MAX, draftReplyKind, fitQuote, forwardSubject, mergeThreadMessages, formatAddress, hasReadableView, parseAddressList, quoteText, replyRecipients, replySubject, isReadableBody, type ReplyTargets } from "../logic/mail.ts";
import { replyFrom } from "../logic/mailIdentity.ts";
import { errorKind } from "../ports.ts";
import type { ComposerStart } from "./MailComposerViewModel.ts";
import { MAIL_THREAD_SNAPSHOT_VERSION, fitMailThreadBodies, type MailCachedBody } from "./MailThreadCache.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/**
 * `view`: what's shown, the original email (sanitized, as Gmail shows it) or the reading view (Mux's: the article in
 * the app's own colors). A message opens the way I chose in Settings → Email (MailNotifyPrefs.view, the original by
 * default; the server applies it, so it follows me across devices). `canRead`: the email has a reading view, so the
 * toggle shows, in both directions.
 */
export type MailBody = { status: "loading" | "ready" | "failed"; html?: string; imagesLoaded: boolean; view: MailBodyView; canRead: boolean };

export type MailThreadViewState = {
  status: "loading" | "ready" | "failed";
  thread?: MailThread;
  messages: MailMessage[];
  /** Expanded messages (the newest and every unread one start expanded, like Gmail). */
  expanded: string[];
  bodies: Record<string, MailBody>;
  /** The sender failed SPF/DKIM/DMARC or the classifier flagged phishing: show the warning banner. */
  suspicious: boolean;
  /** Invite answers on their way (message id → the answer), so the card shows it at once. */
  answering: Record<string, AnswerMailInviteRequest["response"]>;
  /** An invite answer the server refused or that failed (message id): the card says so. */
  answerFailed?: string | undefined;
  /** My saved drafts replying to or forwarding this thread's messages, one per message, newest first. */
  drafts: MailDraft[];
  /** A message's full text couldn't be read (to show it, or to quote it in a reply): the thread says so. */
  textFailed?: boolean | undefined;
  /** Messages that just came in with a refresh: they arrive with an accent, for a moment. */
  newIds: string[];
};

/** Changes close together write the thread once. */
const SAVE_MS = 500;
/** How long a new message keeps its accent (Mail's new rows). */
const NEW_MESSAGE_MS = 1600;

export class MailThreadViewModel extends ViewModel<MailThreadViewState> {
  /** On screen from the device, and the server hasn't answered yet. */
  private fromDevice = false;
  /** Bodies shown from the device, not yet checked against the server. */
  private readonly unchecked = new Set<string>();
  private cancelSave: (() => void) | undefined;
  private cancelNew: (() => void) | undefined;

  /**
   * `myAddress`: the thread's mailbox address; `sendAs`: its sendable addresses (Mailbox.sendAs), so a reply starts
   * from the address the message was sent to and none of my addresses is replied to; `replyMode`: my "When replying"
   * choice (Mailbox.replyFrom; absent = from the address it was sent to).
   */
  constructor(
    private readonly ws: WorkspaceViewModel,
    readonly threadId: string,
    private readonly myAddress: () => string | undefined,
    private readonly sendAs: () => MailSendAs[] | undefined = () => undefined,
    private readonly replyMode: () => MailReplyFrom | undefined = () => undefined,
  ) {
    super({ status: "loading", messages: [], expanded: [], bodies: {}, suspicious: false, answering: {}, drafts: [], newIds: [] });
    this.onDispose(() => {
      this.cancelNew?.();
      // Leaving within the moment still keeps what I read.
      if (this.cancelSave !== undefined) this.save();
      this.cancelSave?.();
    });
    this.restore();
  }

  /** The device's copy, if it has one: on screen at once, as ready. */
  private restore(): void {
    const snapshot = this.ws.mailThreads?.read(this.threadId);
    if (snapshot === undefined) return;
    this.fromDevice = true;
    const bodies: Record<string, MailBody> = {};
    for (const [id, b] of Object.entries(snapshot.bodies)) {
      bodies[id] = { status: "ready", html: b.html, imagesLoaded: imagesByDefault(snapshot.messages.find((m) => m.id === id)), view: b.view, canRead: b.canRead };
      this.unchecked.add(id);
    }
    this.update({ status: "ready", thread: snapshot.thread, messages: snapshot.messages, expanded: snapshot.expanded, bodies, suspicious: isSuspicious(snapshot.thread, snapshot.messages) });
  }

  private saveSoon(): void {
    if (this.ws.mailThreads === undefined || this.isDisposed) return;
    this.cancelSave?.();
    this.cancelSave = this.ws.deps.clock.setTimer(SAVE_MS, () => {
      this.cancelSave = undefined;
      this.save();
    });
  }

  /** Keeps what I'm reading. Images I loaded by hand stay off the device: Show images is asked for on each open. */
  private save(): void {
    const { thread, messages, expanded, bodies } = this.state;
    if (thread === undefined) return;
    const shown: Record<string, MailCachedBody> = {};
    for (const [id, b] of Object.entries(bodies)) {
      if (b.status === "ready" && b.html !== undefined && b.imagesLoaded === imagesByDefault(messages.find((m) => m.id === id))) shown[id] = { html: b.html, view: b.view, canRead: b.canRead };
    }
    this.ws.mailThreads?.write(this.threadId, { v: MAIL_THREAD_SNAPSHOT_VERSION, thread, messages, expanded, bodies: fitMailThreadBodies(messages, shown) }, this.ws.deps.clock.now());
  }

  private markNew(ids: string[]): void {
    this.update({ newIds: ids });
    this.cancelNew?.();
    this.cancelNew = this.ws.deps.clock.setTimer(NEW_MESSAGE_MS, () => {
      this.cancelNew = undefined;
      this.update({ newIds: [] });
    });
  }

  /** A body shown from the device, against the server: only a changed one replaces it, and a failure leaves it. */
  private async recheckBody(messageId: string): Promise<void> {
    this.unchecked.delete(messageId);
    const shown = this.state.bodies[messageId];
    if (shown === undefined) return;
    try {
      const html = await this.ws.deps.api.mailMessageBody(this.ws.wsId, messageId, shown.imagesLoaded, undefined);
      if (this.isDisposed || this.state.bodies[messageId] !== shown || html === shown.html) return;
      const body: MailBody = { ...shown, html, view: isReadableBody(html) ? "readable" : "original", canRead: shown.canRead || hasReadableView(html) };
      this.update({ bodies: { ...this.state.bodies, [messageId]: body } });
      this.saveSoon();
    } catch {
      // It stays as it was.
    }
  }

  async load(): Promise<void> {
    try {
      // Only what's new is read in full: older messages load when opened or quoted (ensureText).
      const known = this.state.messages;
      const fullAfter = Math.max(0, ...known.map((m) => m.sentAt));
      const r = await this.ws.deps.api.getMailThread(this.ws.wsId, this.threadId, { lite: "1", ...(known.length === 0 ? {} : { fullAfter }) });
      if (this.isDisposed) return;
      this.fromDevice = false;
      const messages = mergeThreadMessages(r.messages, this.state.messages);
      const last = r.messages[r.messages.length - 1];
      // A reload (new mail, a status change) keeps what I opened and opens the newest, like Gmail.
      const opened = this.state.thread === undefined ? [] : this.state.expanded;
      const expanded = last === undefined || opened.includes(last.id) ? opened : [...opened, last.id];
      const added = known.length === 0 ? [] : messages.filter((m) => !known.some((k) => k.id === m.id)).map((m) => m.id);
      this.update({ status: "ready", thread: r.thread, messages, expanded, suspicious: isSuspicious(r.thread, r.messages) });
      if (added.length > 0) this.markNew(added);
      for (const id of expanded) {
        if (this.state.bodies[id] === undefined) void this.loadBody(id, false);
        else if (this.unchecked.has(id)) void this.recheckBody(id);
        if (messages.find((m) => m.id === id)?.hasHtml === false) void this.ensureText(id);
      }
      this.saveSoon();
      void this.loadDrafts();
    } catch (e) {
      if (this.isDisposed) return;
      // The thread is gone: so is its copy, and it doesn't stay on screen.
      if (errorKind(e) === "notFound" && this.fromDevice) {
        this.ws.mailThreads?.drop(this.threadId);
        this.update({ status: "failed", thread: undefined, messages: [], expanded: [], bodies: {} });
        return;
      }
      // Only a thread that never opened fails: a failed reload keeps it, and the reply being written, on screen.
      if (this.state.thread === undefined) this.update({ status: "failed" });
    }
  }

  private async loadDrafts(): Promise<void> {
    const thread = this.state.thread;
    if (thread === undefined) return;
    try {
      const all = await this.ws.deps.api.listMailDrafts(this.ws.wsId, thread.mailboxId);
      if (this.isDisposed) return;
      const ids = new Set(this.state.messages.map((m) => m.id));
      const mine = all.filter((d) => d.mode !== "new" && d.sourceMessageId !== undefined && ids.has(d.sourceMessageId)).sort((a, b) => b.updatedAt - a.updatedAt);
      this.update({ drafts: mine.filter((d, i) => mine.findIndex((x) => x.sourceMessageId === d.sourceMessageId) === i) });
    } catch {
      // No drafts to bring back; a reply starts fresh.
    }
  }

  /** A reply box closed: `draft` is what it left (MailComposerViewModel.leftDraft), so Reply brings it back. */
  noteDraft(sourceMessageId: string | undefined, draft: MailDraft | undefined): void {
    const others = this.state.drafts.filter((d) => d.sourceMessageId !== sourceMessageId);
    this.update({ drafts: draft === undefined ? others : [draft, ...others] });
  }

  /** The reply I left here, to reopen when I come back to the thread. */
  resumeStart(): ComposerStart | undefined {
    const d = this.state.drafts[0];
    return d === undefined ? undefined : this.start(d.mode === "forward" ? "forward" : "reply", d.sourceMessageId);
  }

  toggle(messageId: string): void {
    const open = this.state.expanded.includes(messageId);
    this.update({ expanded: open ? this.state.expanded.filter((x) => x !== messageId) : [...this.state.expanded, messageId] });
    if (!open && this.state.bodies[messageId] === undefined) void this.loadBody(messageId, false);
    else if (!open && this.unchecked.has(messageId)) void this.recheckBody(messageId);
    if (!open && this.state.messages.find((m) => m.id === messageId)?.hasHtml === false) void this.ensureText(messageId);
  }

  /** Loads a message's full text if the thread came without it. → false when it couldn't (`textFailed` says so). */
  async ensureText(messageId: string): Promise<boolean> {
    const m = this.state.messages.find((x) => x.id === messageId);
    if (m === undefined) return false;
    if (m.textPartial !== true) return true;
    try {
      const text = await this.ws.deps.api.mailMessageText(this.ws.wsId, messageId);
      if (this.isDisposed) return false;
      this.update({ messages: mergeThreadMessages(this.state.messages, [{ ...m, text, textPartial: false }]), textFailed: false });
      return true;
    } catch {
      if (!this.isDisposed) this.update({ textFailed: true });
      return false;
    }
  }

  /**
   * `start`, once the message's full text is here: a reply never quotes a preview. undefined when it can't be read.
   * A saved draft already holds its quote, so it opens without reading the text.
   */
  async prepare(kind: "reply" | "replyAll" | "forward", messageId?: string, fresh = false): Promise<ComposerStart | undefined> {
    const id = messageId ?? this.state.messages[this.state.messages.length - 1]?.id;
    if (id === undefined) return undefined;
    const saved = !fresh && this.state.drafts.some((d) => d.sourceMessageId === id);
    if (!saved && !(await this.ensureText(id))) return undefined;
    return this.start(kind, id, fresh);
  }

  /** "Load images" for one message (through the image proxy). */
  loadImages(messageId: string): void {
    void this.loadBody(messageId, true, this.state.bodies[messageId]?.view);
  }

  /** The original email ⇄ its reading view, for this message (Settings → Email picks how messages open). */
  toggleView(messageId: string): void {
    const b = this.state.bodies[messageId];
    if (b === undefined || !b.canRead) return;
    void this.loadBody(messageId, b.imagesLoaded, b.view === "readable" ? "original" : "readable");
  }

  /** Realtime: this thread got a new message or changed (status of a sent message, tab, folder). */
  onEvent(ev: PersistentEvent): void {
    if (ev.type !== "mail.thread.updated" || ev.thread.id !== this.threadId) return;
    const known = this.state.thread?.messageCount ?? 0;
    this.update({ thread: ev.thread });
    this.saveSoon();
    if (ev.thread.messageCount !== known || this.state.messages.some((m) => m.status === "queued")) void this.load();
  }

  /**
   * Yes / No / Maybe on a message's calendar invite (Gmail's card): shown at once, then the server mails the REPLY
   * to the organizer and the card comes back fresh (my calendar copy has the answer). `scope` for a repeating event.
   */
  async answerInvite(messageId: string, response: AnswerMailInviteRequest["response"], scope?: AnswerMailInviteRequest["scope"]): Promise<void> {
    const { [messageId]: _, ...others } = this.state.answering;
    void _;
    this.update({ answering: { ...others, [messageId]: response }, answerFailed: undefined });
    try {
      const invite = await this.ws.deps.api.answerMailInvite(this.ws.wsId, messageId, scope === undefined ? { response } : { response, scope });
      if (this.isDisposed) return;
      const { [messageId]: __, ...rest } = this.state.answering;
      void __;
      this.update({ messages: this.state.messages.map((m) => (m.id === messageId ? { ...m, invite } : m)), answering: rest });
    } catch {
      if (this.isDisposed) return;
      const { [messageId]: __, ...rest } = this.state.answering;
      void __;
      this.update({ answering: rest, answerFailed: messageId });
    }
  }

  /**
   * What Reply / Reply all / Forward start with for a message (the newest when omitted): my saved draft for it if I
   * left one, one draft per message. `fresh`: a new message whatever is saved (Propose new time).
   */
  start(kind: "reply" | "replyAll" | "forward", messageId?: string, fresh = false): ComposerStart | undefined {
    const m = messageId === undefined ? this.state.messages[this.state.messages.length - 1] : this.state.messages.find((x) => x.id === messageId);
    const me = this.myAddress();
    if (m === undefined || this.state.thread === undefined) return undefined;
    const mailboxId = this.state.thread.mailboxId;
    const when = new Date(m.sentAt).toISOString().slice(0, 16).replace("T", " ");
    const attribution = `${when}, ${formatAddress(m.from)}:`;
    const quote = quoteText(m.text, attribution);
    const forwardFiles = m.attachments.filter((a) => !a.inline).slice(0, 20).map((a) => ({ fileId: a.id, name: a.filename, size: a.size }));
    const sendAs = this.sendAs() ?? [];
    const mine = [me ?? "", ...sendAs.map((s) => s.address)].filter((a) => a !== "");
    const targets: ReplyTargets = { reply: replyRecipients(m, mine, false), replyAll: replyRecipients(m, mine, true), subject: replySubject(m.subject), forwardSubject: forwardSubject(m.subject) };
    const draft = fresh ? undefined : this.state.drafts.find((d) => d.sourceMessageId === m.id);
    if (draft !== undefined) {
      const saved = draftReplyKind(draft.mode, parseAddressList(draft.to), parseAddressList(draft.cc), targets);
      return { mailboxId, mode: draft.mode, sourceMessageId: m.id, to: [], cc: [], subject: draft.subject, text: draft.text, draft, kind: saved, ...(kind === "replyAll" && saved === "reply" ? { switchTo: kind } : {}), targets, forwardFiles };
    }
    if (m.textPartial === true) return undefined;
    if (kind === "forward") {
      return { mailboxId, mode: "forward", sourceMessageId: m.id, to: [], cc: [], attachments: forwardFiles, subject: targets.forwardSubject, text: `\n\n${fitQuote(quote, FORWARD_QUOTE_MAX)}`, kind, ...(fresh ? {} : { targets, forwardFiles }) };
    }
    const start: ComposerStart = { mailboxId, mode: "reply", sourceMessageId: m.id, ...targets[kind], subject: targets.subject, text: `\n\n${fitQuote(quote, REPLY_QUOTE_MAX)}`, kind, ...(fresh ? {} : { targets, forwardFiles }) };
    const from = sendAs.length > 1 ? replyFrom(m, sendAs, this.replyMode()) : undefined;
    if (from !== undefined) start.from = from;
    return start;
  }

  attachmentUrl(messageId: string, attachmentId: string): string {
    return this.ws.deps.api.mailAttachmentUrl(this.ws.wsId, messageId, attachmentId);
  }

  /** `view` undefined: the way I chose messages open (the server knows it). */
  private async loadBody(messageId: string, images: boolean, view?: MailBodyView): Promise<void> {
    const m = this.state.messages.find((x) => x.id === messageId);
    if (m === undefined) return;
    const prev = this.state.bodies[messageId];
    const canRead = prev?.canRead ?? false;
    const shown = view ?? prev?.view ?? "original";
    // Gmail: remote images show (through Bundle's proxy) unless the mail is spam or suspicious.
    images ||= imagesByDefault(m);
    this.unchecked.delete(messageId);
    this.update({ bodies: { ...this.state.bodies, [messageId]: { status: "loading", imagesLoaded: images, view: shown, canRead } } });
    try {
      const html = await this.ws.deps.api.mailMessageBody(this.ws.wsId, messageId, images, view);
      const body: MailBody = { status: "ready", html, imagesLoaded: images, view: isReadableBody(html) ? "readable" : "original", canRead: canRead || hasReadableView(html) };
      if (this.isDisposed) return;
      this.update({ bodies: { ...this.state.bodies, [messageId]: body } });
      this.saveSoon();
    } catch {
      if (this.isDisposed) return;
      this.update({ bodies: { ...this.state.bodies, [messageId]: { status: "failed", imagesLoaded: images, view: shown, canRead } } });
      // The failed body falls back to the plain text.
      void this.ensureText(messageId);
    }
  }
}

/** The sender failed SPF/DKIM/DMARC or the classifier flagged phishing. */
/** Remote images load on open unless the server blocked them (spam, phishing, failed authentication). */
function imagesByDefault(m: MailMessage | undefined): boolean {
  return m !== undefined && !m.remoteImages;
}

function isSuspicious(thread: MailThread, messages: MailMessage[]): boolean {
  const lastIn = [...messages].reverse().find((m) => m.direction === "in");
  return thread.phishing === true || (lastIn?.auth !== undefined && (lastIn.auth.dmarc === "fail" || (lastIn.auth.spf === "fail" && lastIn.auth.dkim !== "pass")));
}
